import { AttributeSelection } from '@dispatch/shared/types';
import type {
  AttributeForm,
  GeneralInformation,
  LocationFormBulk,
  Resource,
  ResourceBulkForm,
  SelectedAttributeGroup,
  TeamsOfficesRoles,
} from '@dispatch/shared/types';

/**
 * Which Bulk Update tabs can be seeded from a copy source. "Don't copy" is
 * keyed by these names on `ResourceBulkForm.dontCopy`. Schedules/events are not
 * copied from a source, so the Schedules tab has no copy controls.
 *
 * `attributes` covers the whole Attributes tab, which is individual attributes
 * XOR attribute groups — one toggle governs both kinds, so a copy seeds only
 * whichever kind the source is in (see `resolveSourceAttributeKind`).
 */
export type CopyableTab =
  | 'general'
  | 'locations'
  | 'teamsOfficesRoles'
  | 'attributes';

/**
 * Maps a source resource's general setup onto the bulk form shape.
 *
 * Only the fields the bulk General tab actually edits are copied. Identity
 * fields (name/email/description/phone/externalResourceId) are NOT part of the
 * `BulkUpdatePostBody.generalInformation` contract and have no bulk control, so
 * they are left blank — `removeEmptyValues` then drops them from the payload
 * rather than broadcasting the source resource's identity/PII to every target.
 */
export function mapGeneralInformation(source: Resource): GeneralInformation {
  return {
    name: '',
    email: '',
    externalResourceId: '',
    commuteTimeInMin: source.commuteTimeInMin,
    wipLimit: source.wipLimit,
    claimCapacity: source.claimCapacity,
    virtualClaimCapacity: source.virtualClaimCapacity,
    complexLossClaimCapacity: source.complexLossClaimCapacity,
    efficiency: source.efficiency,
    isWorkTeam: source.isWorkTeam,
    lockImmediately: source.lockImmediately,
    isStandby: source.isStandby,
    isAlwaysAvailable: source.isAlwaysAvailable,
  };
}

/**
 * Maps a source resource's Claim Owner (per-day) capacity onto the bulk form.
 *
 * Owner capacity lives on the General tab but is a top-level `claimOwner` form
 * field (siblings of the Handler capacities in `generalInformation`), so it is
 * seeded separately. Values pass through as-is — absence stays absent (like the
 * Handler capacities in `mapGeneralInformation`) so a source with no owner
 * capacity omits the section from the payload rather than zeroing every target,
 * and the Confirm tab renders nothing instead of bare `0`s. A genuine stored
 * `0` still passes through. `calculatedSumOfCapacities` is a single-resource-only
 * running total, intentionally omitted so it never reaches the bulk payload.
 */
export function mapClaimOwner(
  source: Resource
): ResourceBulkForm['claimOwner'] {
  const capacity = source.claimOwnerCapacity;
  return {
    onSunday: capacity?.onSunday,
    onMonday: capacity?.onMonday,
    onTuesday: capacity?.onTuesday,
    onWednesday: capacity?.onWednesday,
    onThursday: capacity?.onThursday,
    onFriday: capacity?.onFriday,
    onSaturday: capacity?.onSaturday,
    maxWeeklyCapacity: capacity?.maxWeeklyCapacity,
  };
}

/** Maps a source resource's team/office/role memberships onto the bulk form. */
export function mapTeamsOfficesRoles(source: Resource): TeamsOfficesRoles {
  return {
    teams: (source.teams ?? []).map((team) => team.integrationGUID),
    offices: (source.offices ?? []).map((office) => office.integrationGUID),
    roles: (source.roles ?? []).map((role) => role.integrationGUID),
    isContingent: source.isContingent,
    contingentCompany: source.contingentCompany ?? undefined,
  };
}

/** Maps a source resource's locations onto the bulk form location shape. */
export function mapLocations(source: Resource): LocationFormBulk[] {
  return (source.locations ?? []).map((location) => ({
    integrationGUID: location.integrationGUID,
    locationInformation: {
      name: location.name,
      street: location.street,
      unit: location.unit,
      city: location.city,
      state: location.state,
      zip: location.zip,
      lat: location.lat,
      long: location.long,
      isVirtualLocation: String(location.isVirtualLocation ?? false),
    },
    serviceInformation: {
      startDate: location.startDate,
      endDate: location.endDate,
      allowedTravelRadius: location.allowedTravelRadius,
      monday: location.monday,
      tuesday: location.tuesday,
      wednesday: location.wednesday,
      thursday: location.thursday,
      friday: location.friday,
      saturday: location.saturday,
      sunday: location.sunday,
      isStartingLocation: location.isStartingLocation,
      isVirtualLocation: String(location.isVirtualLocation ?? false),
    },
    exclusionZonesInformation: location.exclusionZones,
    inclusionZonesInformation: location.inclusionZones,
  }));
}

/** Maps a source resource's attribute definitions onto the bulk form. */
export function mapAttributes(source: Resource): AttributeForm[] {
  return (source.selectedAttributeDefinitions ?? []).map((definition) => ({
    integrationGUID: definition.integrationGUID,
    displayName: definition.displayName,
    attributeDefinitionIntegrationGUID:
      definition.attributeDefinitionIntegrationGUID,
    selectedAttributeValuesVarchars: (
      definition.selectedAttributeValuesVarchars ?? []
    ).map((varchar) => ({
      integrationGUID: varchar.integrationGUID,
      selectedAttributeDefinitionIntegrationGUID:
        varchar.selectedAttributeDefinitionIntegrationGUID,
      attributeDefinitionValueIntegrationGUID:
        varchar.attributeDefinitionValueIntegrationGUID,
      title: varchar.displayName,
      value: varchar.value,
      displayName: varchar.displayName,
      startDate: varchar.startDate,
      endDate: varchar.endDate,
      subAttributes: varchar.subAttributes,
    })),
    selectedAttributeValuesBools: mapFirstBool(
      definition.selectedAttributeValuesBools
    ),
  }));
}

/** Maps a source resource's selected attribute groups onto the bulk form. */
export function mapAttributeGroups(source: Resource): SelectedAttributeGroup[] {
  return (source.selectedAttributeGroups ?? []).map((group) => ({
    id: group.id,
    displayName: group.displayName,
    startDate: group.startDate ?? null,
    endDate: group.endDate ?? null,
  }));
}

/**
 * Which attribute kind a copy source is in. Mirrors how the details form
 * (`ResourcesDetails.tsx`) resolves `attributeFilterSelection` when building its
 * defaults: the persisted flag wins, and a null flag falls back to "groups if any
 * are populated". An explicit Single flag therefore never copies
 * stored-but-inactive groups — the backend deliberately retains the other kind's
 * rows when a resource's mode flips.
 *
 * This duplicates that resolution rather than sharing it, so the two can drift.
 * Pointing the details form at this helper would be the real fix; it is out of
 * scope here because that form is not otherwise touched.
 */
export function resolveSourceAttributeKind(
  source: Resource
): 'attributes' | 'groups' {
  const selection =
    source.attributeFilterSelection ??
    (source.selectedAttributeGroups?.length
      ? AttributeSelection.Group
      : AttributeSelection.Single);
  return selection === AttributeSelection.Group ? 'groups' : 'attributes';
}

type BoolForm = AttributeForm['selectedAttributeValuesBools'];

/** The bulk form models a single bool attribute value, not an array. */
function mapFirstBool(
  bools: Resource['selectedAttributeDefinitions'][number]['selectedAttributeValuesBools']
): BoolForm {
  const first = bools?.[0];
  if (!first) {
    return {} as BoolForm;
  }

  return {
    integrationGUID: first.integrationGUID,
    selectedAttributeDefinitionIntegrationGUID:
      first.selectedAttributeDefinitionIntegrationGUID,
    attributeDefinitionValueIntegrationGUID:
      first.attributeDefinitionValueIntegrationGUID,
    title: first.displayName,
    displayName: first.displayName,
    value: first.value,
    startDate: first.startDate,
    endDate: first.endDate,
  };
}
